import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, Button, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WorkIcon from '@mui/icons-material/Work';
import WeekendIcon from '@mui/icons-material/Weekend';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BaseModal from '../templates/BaseModal';

const WHATSAPP_NUMBER = '56229140663';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    bgcolor: 'background.paper',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    pt: 2.5,
    pb: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    flexShrink: 0,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconColor: { fontSize: 20, color: 'text.secondary' },
  headerText: { flexGrow: 1 },
  headerTitle: { fontWeight: 600, color: 'text.primary', lineHeight: 1.3 },
  closeButton: { color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } },
  body: {
    flexGrow: 1,
    px: 2.5,
    py: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  },
  robotImage: {
    height: 80,
    width: 'auto',
    alignSelf: 'center',
  },
  description: {
    fontSize: '0.9rem',
    color: 'text.secondary',
    textAlign: 'justify',
    lineHeight: 1.6,
  },
  timeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
  },
  timeIcon: { color: 'text.secondary', mt: 0.25 },
  timeLabel: {
    fontSize: '0.875rem',
    color: 'text.primary',
    fontWeight: 500,
  },
  timeSub: {
    fontSize: '0.8rem',
    color: 'text.secondary',
    mt: 0.25,
  },
  footer: {
    px: 2.5,
    pb: 2.5,
    pt: 1,
    flexShrink: 0,
  },
  whatsappButton: {
    color: '#25D366',
    borderColor: '#25D366',
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    py: 1.25,
    '&:hover': { borderColor: '#1ebe57', color: '#1ebe57', bgcolor: 'transparent' },
  },
};

export default function SupportModal({ open, onClose, currentChain, currentStoreName }) {
  const { t } = useTranslation();

  const handleWhatsApp = () => {
    const message = `${t('bruno.support.help_message')} ${currentChain} ${currentStoreName}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <BaseModal open={open} onClose={onClose} dialogWidth={480} dialogHeight="auto">
      <Box sx={styles.root}>

        {/* Header */}
        <Box sx={styles.headerRow}>
          <Box sx={styles.headerIcon}>
            <HelpOutlineIcon sx={styles.headerIconColor} />
          </Box>
          <Box sx={styles.headerText}>
            <Typography variant="body1" sx={styles.headerTitle}>
              {t('bruno.support.title')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={styles.body}>
          <Box
            component="img"
            src="/bruno_robot_sm.png"
            alt="Bruno"
            sx={styles.robotImage}
          />

          <Typography sx={styles.description}>
            {t('bruno.support.description')}
          </Typography>

          <Divider />

          {/* Weekday hours */}
          <Box sx={styles.timeRow}>
            <WorkIcon sx={styles.timeIcon} />
            <Box>
              <Typography sx={styles.timeLabel}>
                {t('bruno.support.weeday_times')}
              </Typography>
              <Typography sx={styles.timeSub}>
                {t('bruno.support.weekday_description')}
              </Typography>
            </Box>
          </Box>

          {/* Weekend hours */}
          <Box sx={styles.timeRow}>
            <WeekendIcon sx={styles.timeIcon} />
            <Box>
              <Typography sx={styles.timeLabel}>
                {t('bruno.support.weekend_times')}
              </Typography>
              <Typography sx={styles.timeSub}>
                {t('bruno.support.weekend_description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer — WhatsApp button */}
        <Box sx={styles.footer}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleWhatsApp}
            sx={styles.whatsappButton}
            startIcon={<WhatsAppIcon />}
          >
            {t('bruno.support.whatsapp')}
          </Button>
        </Box>

      </Box>
    </BaseModal>
  );
}
